// Admin Console audit & compliance client (PB-1588 domain 1).
//
// Two endpoints, deliberately kept distinct rather than merged behind one
// call: access history (PB-13) is the curated "who gained or lost access"
// view, and audit search is the whole tenant record. They answer different
// questions and a reader needs to know which one they are looking at.
//
// Audit search reads the platform's tenant-wide record (`GET /v1/audit`,
// ADR-0061), which is identity-blind, and names its people and teams through
// Auth's resolve route: one POST per page, never one per row (PB-4773). Access
// history stays on Auth.
//
// Reuses `request` from admin-users-api rather than restating the CSRF +
// credentials + error-envelope handling.

import {
  auditSubjectName,
  auditSubjectTeamId,
  auditSubjectUserId,
} from "@playbookai/types";
import { entityReference } from "../pages/admin/audit-format.js";
import { request } from "./admin-users-api.js";

/** One row of the tenant-wide audit record. */
export interface AuditEvent {
  id: string;
  ts: string; // ISO-8601
  actorId: string | null;
  /** Resolved by Auth for the whole page, or the platform's own name for its
      system actor. Null for someone who has since left the tenant — the record
      outlives membership. */
  actorName: string | null;
  actorEmail: string | null;
  /** Who or what the row is about, in words, by the shared subject rule
      (`auditSubjectName`): a person, a team, "Person (Team)" for a team
      member or request, or an invitation's address. Null when nothing names
      it. */
  entityName: string | null;
  /** The service that wrote the row — absent from access history, where the
      writer is implied. */
  service: string;
  action: string;
  entityType: string;
  entityId: string;
  payload: unknown;
}

/** One row of access history. Same shape minus `service`. */
export interface AccessEvent {
  id: string;
  ts: string;
  actorId: string | null;
  /** Resolved by the server for the whole page. `null` means the actor is no
      longer in the tenant — which is a DIFFERENT statement from "not looked
      up", and the console says so. */
  actorName: string | null;
  actorEmail: string | null;
  action: string;
  entityType: string;
  entityId: string;
  payload: unknown;
}

export interface AuditEventPage {
  data: AuditEvent[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface AccessEventPage {
  data: AccessEvent[];
  nextCursor: string | null;
  hasMore: boolean;
}

/** Server-side filters for the audit search. Every field is optional; omitting
    all of them returns the tenant's whole record, newest first. */
export interface AuditSearchFilters {
  readonly entityType?: string;
  readonly action?: string;
  readonly actorId?: string;
  readonly service?: string;
  /** ISO-8601. Inclusive. */
  readonly from?: string;
  /** ISO-8601. Inclusive to the end of the millisecond. */
  readonly to?: string;
  /** Leave routine activity out (PB-5410). A string, because only strings
      reach the wire here; a saved export job only ever echoes `"true"`. */
  readonly hideRoutine?: "true" | "false";
}

/* Within both endpoints' ceilings: Auth's access log clamps above its
   MAX_LIMIT, and `/v1/audit` refuses a `limit` above 100 outright. */
const DEFAULT_PAGE_SIZE = 50;

/** A row as `GET /v1/audit` returns it tenant-wide: redacted, and naming no
    one but the platform's own system actor. */
interface PlatformAuditEntry {
  readonly id: string;
  readonly ts: string;
  readonly actorId: string | null;
  readonly actorName: string | null;
  readonly actorKind?: "system";
  readonly service: string;
  readonly action: string;
  readonly entityType: string;
  readonly entityId: string;
  readonly payload: Record<string, unknown> | null;
}

interface PlatformAuditPage {
  readonly data: readonly PlatformAuditEntry[];
  readonly nextCursor: string | null;
  readonly hasMore: boolean;
}

interface PlatformAuditFacets {
  readonly actions: readonly string[];
  readonly entityTypes: readonly string[];
  readonly services: readonly string[];
  /** `actorName` is set only for the system actor. */
  readonly actors: readonly {
    readonly id: string;
    readonly actorName: string | null;
  }[];
  readonly truncated: AuditFacetTruncation;
}

/** The ids one page of audit rows carries. An absent list asks for nothing. */
export interface AuditIdentityRequest {
  readonly actorIds?: readonly string[];
  readonly subjectIds?: readonly string[];
  readonly teamIds?: readonly string[];
}

export interface AuditIdentity {
  readonly userId: string;
  /** Never null for an actor: an actor is answered only while an active
      member, and a nameless one as "Unnamed member". */
  readonly displayName: string | null;
  readonly email: string | null;
}

export interface AuditTeamName {
  readonly teamId: string;
  readonly name: string;
}

/** Who the ids name. An id that names nobody in this tenant is absent. */
export interface AuditIdentities {
  readonly actors: readonly AuditIdentity[];
  readonly subjects: readonly AuditIdentity[];
  readonly teams: readonly AuditTeamName[];
}

export async function resolveAuditIdentities(
  ids: AuditIdentityRequest,
): Promise<AuditIdentities> {
  return (await request("/api/auth/admin/audit-identities/resolve", {
    method: "POST",
    body: JSON.stringify(ids),
  })) as AuditIdentities;
}

const NO_IDENTITIES: AuditIdentities = { actors: [], subjects: [], teams: [] };

/* Ids are compared lower-cased. The resolve route lower-cases what it is asked
   and refuses a list that repeats an id in any spelling, and Postgres answers
   in lower case, but a payload may spell a uuid either way: compared as
   written, one id spelled two ways would fail the page, and a capitalised one
   would never find its name. */
const lower = (id: string) => id.toLowerCase();

/** A name lookup keyed lower-case: every key is lowered on the way in and on
    the way out, so it stores and answers in whichever case it is given. */
class LowerKeyedMap<V> extends Map<string, V> {
  constructor(entries: Iterable<readonly [string, V]>) {
    // Map's constructor adds each entry through `set`, which lowers it.
    super(entries);
  }
  override set(key: string, value: V): this {
    return super.set(lower(key), value);
  }
  override get(key: string): V | undefined {
    return super.get(lower(key));
  }
  override has(key: string): boolean {
    return super.has(lower(key));
  }
}

/* The resolve route's per-list maximum: `maxItems: 500` in auth.yaml
   (`AUDIT_IDENTITY_REQUEST_MAX`). A page names at most its own rows; only the
   facets' actor list can come near it. */
const RESOLVE_LIST_MAX = 500;

/* Asks only when there is something to ask about, so an empty page costs one
   request, not two. A failed lookup fails the caller: guessing would show a
   current member as departed (PB-4773 D13). */
function resolveIfAny(ids: {
  readonly actorIds: ReadonlySet<string>;
  readonly subjectIds?: ReadonlySet<string>;
  readonly teamIds?: ReadonlySet<string>;
}): Promise<AuditIdentities> {
  const actorIds = [...ids.actorIds];
  const subjectIds = [...(ids.subjectIds ?? [])];
  const teamIds = [...(ids.teamIds ?? [])];
  if (actorIds.length + subjectIds.length + teamIds.length === 0) {
    return Promise.resolve(NO_IDENTITIES);
  }
  return resolveAuditIdentities({
    ...(actorIds.length > 0 ? { actorIds } : {}),
    ...(subjectIds.length > 0 ? { subjectIds } : {}),
    ...(teamIds.length > 0 ? { teamIds } : {}),
  });
}

export async function searchAuditEvents(
  cursor?: string | null,
  filters: AuditSearchFilters = {},
  limit = DEFAULT_PAGE_SIZE,
): Promise<AuditEventPage> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set("cursor", cursor);
  /* Filtering is server-side: the audit record is unbounded, so any
     client-side filter over the loaded pages would silently search a
     fraction of it and present the result as complete. */
  for (const [key, value] of Object.entries(filters)) {
    const trimmed = typeof value === "string" ? value.trim() : "";
    if (trimmed) params.set(key, trimmed);
  }
  const page = (await request(
    `/api/v1/audit?${params.toString()}`,
  )) as PlatformAuditPage;

  /* Which ids a row needs, and how its subject is worded from them, is the
     rule Auth's export and Identity Activity use too (`@playbookai/types`), so
     a person or team reads one way on every surface. */
  const actorIds = new Set<string>();
  const subjectIds = new Set<string>();
  const teamIds = new Set<string>();
  for (const row of page.data) {
    if (row.actorId && row.actorKind !== "system") {
      actorIds.add(lower(row.actorId));
    }
    const subjectId = auditSubjectUserId(row);
    if (subjectId) subjectIds.add(lower(subjectId));
    const teamId = auditSubjectTeamId(row);
    if (teamId) teamIds.add(lower(teamId));
  }
  const resolved = await resolveIfAny({ actorIds, subjectIds, teamIds });
  const actors = new LowerKeyedMap(resolved.actors.map((a) => [a.userId, a]));
  const names = {
    people: new LowerKeyedMap(
      resolved.subjects.map((s) => [s.userId, s.displayName]),
    ),
    teams: new LowerKeyedMap(resolved.teams.map((t) => [t.teamId, t.name])),
  };

  return {
    data: page.data.map((row) => {
      const actor = row.actorId ? actors.get(row.actorId) : undefined;
      return {
        id: row.id,
        ts: row.ts,
        actorId: row.actorId,
        actorName: actor?.displayName ?? row.actorName,
        actorEmail: actor?.email ?? null,
        entityName: auditSubjectName(row, names),
        service: row.service,
        action: row.action,
        entityType: row.entityType,
        entityId: row.entityId,
        payload: row.payload,
      };
    }),
    nextCursor: page.nextCursor,
    hasMore: page.hasMore,
  };
}

export async function listAccessEvents(
  cursor?: string | null,
): Promise<AccessEventPage> {
  const params = new URLSearchParams({ limit: String(DEFAULT_PAGE_SIZE) });
  if (cursor) params.set("before", cursor);
  return (await request(
    `/api/auth/admin/access-log?${params.toString()}`,
  )) as AccessEventPage;
}

/** The filter values this tenant's record actually contains. Empty lists mean
    the tenant has produced no such rows — not that filtering is unsupported. */
export interface AuditActorOption {
  /** Wire value for the filter. Never rendered. */
  id: string;
  name: string;
}

export interface AuditFacets {
  actions: string[];
  entityTypes: string[];
  services: string[];
  actors: AuditActorOption[];
  /** Per kind: true when THAT list was capped server-side, so it is a PREFIX
      rather than the whole set. Per kind rather than one flag, so a complete
      list is never labelled incomplete because a different one was capped. */
  truncated: AuditFacetTruncation;
}

export interface AuditFacetTruncation {
  actions: boolean;
  entityTypes: boolean;
  services: boolean;
  actors: boolean;
}

export async function getAuditFacets(): Promise<AuditFacets> {
  const facets = (await request("/api/v1/audit/facets")) as PlatformAuditFacets;
  /* Actors are offered by NAME, and a departed actor is still offered — the
     record outlives membership — under the same stable reference its rows
     show, so the two can be matched by eye.

     Asked in batches of the resolve route's per-list maximum, so the list may
     outgrow one request — the platform's facet cap (`AUDIT_FACET_CAP`) equals
     that maximum today — without failing a large tenant's filter bar. Any
     failed batch fails the facets, as one failed request would (D13). */
  const people = [
    ...new Set(
      facets.actors.filter((a) => a.actorName === null).map((a) => lower(a.id)),
    ),
  ];
  const batches: string[][] = [];
  for (let start = 0; start < people.length; start += RESOLVE_LIST_MAX) {
    batches.push(people.slice(start, start + RESOLVE_LIST_MAX));
  }
  const answers = await Promise.all(
    batches.map((actorIds) => resolveAuditIdentities({ actorIds })),
  );
  const names = new LowerKeyedMap(
    answers.flatMap((answer) =>
      answer.actors.map((a) => [a.userId, a.displayName] as const),
    ),
  );
  return {
    actions: [...facets.actions],
    entityTypes: [...facets.entityTypes],
    services: [...facets.services],
    actors: facets.actors
      .map((actor) => ({
        id: actor.id,
        name:
          names.get(actor.id) ??
          actor.actorName ??
          `Former member · ${entityReference(actor.id)}`,
      }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    truncated: { ...facets.truncated },
  };
}

export type AuditExportStatus =
  | "queued"
  | "processing"
  | "ready"
  | "failed"
  | "expired";

export interface AuditExportJob {
  readonly jobId: string;
  readonly status: AuditExportStatus;
  readonly filters: AuditSearchFilters;
  readonly progress: number;
  readonly rowCount: number | null;
  readonly truncated: boolean | null;
  readonly filename: string | null;
  readonly sizeBytes: string | null;
  readonly errorCode: string | null;
  readonly createdAt: string;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
  readonly expiresAt: string | null;
}

export interface AuditExportDownloadGrant {
  readonly url: string;
  readonly expiresAt: string;
}

export async function createAuditExport(
  filters: AuditSearchFilters = {},
): Promise<AuditExportJob> {
  return (await request("/api/auth/admin/audit-exports", {
    method: "POST",
    body: JSON.stringify(filters),
  })) as AuditExportJob;
}

export async function getAuditExport(jobId: string): Promise<AuditExportJob> {
  return (await request(
    `/api/auth/admin/audit-exports/${encodeURIComponent(jobId)}`,
  )) as AuditExportJob;
}

export async function getAuditExportDownload(
  jobId: string,
): Promise<AuditExportDownloadGrant> {
  return (await request(
    `/api/auth/admin/audit-exports/${encodeURIComponent(jobId)}/download`,
  )) as AuditExportDownloadGrant;
}

/* Tenant-scoped keys. The admin tree stays mounted across a tenant/session
   handover, so an unscoped key lets the next administrator read the previous
   tenant's cached compliance data out of the query cache — the mandatory
   isolation rule applies to client cache, not only to SQL. */
export const auditQueryKeys = {
  facets: (tenantId: string | null | undefined) =>
    ["admin", tenantId ?? "none", "audit-log", "facets"] as const,
  search: (tenantId: string | null | undefined, filters: unknown) =>
    ["admin", tenantId ?? "none", "audit-log", filters] as const,
  access: (tenantId: string | null | undefined) =>
    ["admin", tenantId ?? "none", "access-log"] as const,
  export: (tenantId: string | null | undefined, jobId: string) =>
    ["admin", tenantId ?? "none", "audit-exports", jobId] as const,
};
